import * as R from 'ramda';
import { connect } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { pure, compose, withHandlers } from 'react-recompose';
// components
import { openModal, closeModal } from '../../../components/modal/actions';
import { openLoader, closeLoader } from '../../../components/loader/actions';
// features
import { getOrderRateInfo } from '../../new-do/hocs/with-trip-rates';
import { getLoadDetailsRequest } from '../../dispatch-board/load/actions';
import { getOrderDetailsRequest } from '../../dispatch-board/order/actions';
import {
  getLoadLegRateListRequest,
  getLoadCustomerRateListRequest,
} from '../../dispatch-details/load/actions';
import {
  getOrderLegRateListRequest,
  getOrderCostAllocationsRequest,
  getOrderCustomerRateListRequest,
} from '../../dispatch-details/order/actions';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature rate
import AvailableRates from './available-rates';
import { mapCarrierRatesWithProperQuantity } from '../helpers';
import { withShowIntegrationErrorsOnAvailableRates } from '../hocs';
import { getCarrierRateFields, SelectRateFromIntegration } from '../carrier';
//////////////////////////////////////////////////

const distanceOverrideUomMap = {
  [GC.UOM_MILES]: GC.UOM_MILE,
  [GC.UOM_KILOMETERS]: GC.UOM_KILOMETER,
};

const getRates = R.map((data) => {
  const carrierSnapshot = R.prop(GC.SYSTEM_OBJECT_CARRIER_SNAPSHOT, data);
  const equipments = R.pathOr([], ['price', GC.FIELD_CARRIER_EQUIPMENTS], data);
  const serviceTypes = R.pathOr([], ['price', GC.FIELD_CARRIER_SERVICE_TYPES], data);
  const mode = R.pathOr('', ['price', GC.FIELD_MODE, GC.FIELD_DISPLAYED_VALUE], data);

  return {
    ...data,
    ...carrierSnapshot,
    mode,
    equipments,
    serviceTypes,
  };
});

const useAsyncCloRateAvailableRates = (props) => {
  const {
    cloGuid,
    telGuid,
    loadData,
    isLegRate,
    equipments,
    branchGuid,
    serviceType,
    transportationMode,
    customerEnterpriseGuid,
  } = props;

  const [state, setState] = useState({ loading: true, rateInfo: null, availableRates: [] });

  useEffect(() => {
    const getAvailableRatesRequest = async () => {
      const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();

      const commonData = {
        loadData,
        equipments,
        serviceType,
        transportationMode,
        customerEnterpriseGuid,
        [GC.BRANCH_GUID]: R.or(branchGuid, currentUserBranchGuid),
      };

      const requestData = G.ifElse(
        isLegRate,
        R.mergeRight(commonData, { cloGuid, telGuid }),
        R.assoc(GC.FIELD_LOAD_GUID, cloGuid, commonData),
      );

      const options = { data: requestData };

      const endpoint = G.ifElse(
        isLegRate,
        endpointsMap.legRateAvailableCarriers,
        endpointsMap.cloRateAvailableCarriers,
      );

      const res = await sendRequest('post', endpoint, options);

      const { data, status } = res;

      if (R.and(G.isResponseSuccess(status), G.isNotEmpty(data))) {
        const rates = getRates(data.customerRateData);
        const mapped = mapCarrierRatesWithProperQuantity(rates);

        setState((prev) => ({ ...prev, loading: false, rateInfo: data, availableRates: mapped }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));

        G.handleFailResponseSimple(
          res,
          true,
          'useAsyncCloRateAvailableRates -> getAvailableRatesRequest',
        );
      }
    };

    getAvailableRatesRequest();
  }, []);

  return state;
};

const withAsyncCloRateAvailableRates = (Component) => (props) => {
  const {
    cloGuid,
    telGuid,
    loadData,
    isLegRate,
    branchGuid,
    equipments,
    serviceType,
    transportationMode,
    customerEnterpriseGuid,
  } = props;

  const cloRateAvailableRates = useAsyncCloRateAvailableRates({
    cloGuid,
    telGuid,
    loadData,
    isLegRate,
    branchGuid,
    equipments,
    serviceType,
    transportationMode,
    customerEnterpriseGuid,
  });

  return <Component {...props} {...cloRateAvailableRates} />;
};

const availableRatesEnhance = compose(
  withAsyncCloRateAvailableRates,
  withShowIntegrationErrorsOnAvailableRates,
);

const EnhancedAvailableRates = availableRatesEnhance(AvailableRates);

const mapCharges = (charges) => R.map(
  (item) => R.assoc(GC.FIELD_ID, R.or(G.getGuidFromObject(item), G.genShortId()), item),
  charges,
);

const mapCharges2 = (charges) => R.map(
  (item) => R.assoc(GC.FIELD_ID, R.or(G.getGuidFromObject(item), G.genShortId()), item),
  G.addOneFuelChargeToCharges(charges),
);

const withHandleCloRateAvailableRates = compose(
  connect(
    null,
    {
      openModal,
      closeModal,
      openLoader,
      closeLoader,
    },
  ),
  withHandlers({
    handleSelectRate: (props) => (data, rateInfo) => {
      const {
        values,
        setValues,
        openModal,
        closeModal,
        setCarrierRateToStore,
        handleGetContractAccessorials,
        handleCalculateETAOnRateSelect,
        handleSetRecalculateAllChargesId,
        handleSetCustomerRateFromAvailableRates,
      } = props;

      const fallbackTotalTripDistance = R.or(
        R.path([GC.FIELD_TOTAL_TRIP_DISTANCE], rateInfo),
        R.path([GC.FIELD_TOTAL_TRIP_DISTANCE], values),
      );

      const fallbackTotalTripDistanceUom = R.or(
        R.path([GC.FIELD_TOTAL_TRIP_DISTANCE_UOM], rateInfo),
        R.path([GC.FIELD_TOTAL_TRIP_DISTANCE_UOM], values),
      );

      const distanceOverride = R.path([GC.FIELD_PRICE, GC.FIELD_CUSTOMER_RATE_DISTANCE_OVERRIDE], data);

      const distanceOverrideUom = R.path(
        [GC.FIELD_PRICE, GC.FIELD_CUSTOMER_RATE_DISTANCE_OVERRIDE_UOM],
        data,
      );

      const hasDistanceOverride = G.isAllNotNilOrNotEmpty([distanceOverride, distanceOverrideUom]);

      const totalTripDistance = G.ifElse(
        hasDistanceOverride,
        distanceOverride,
        fallbackTotalTripDistance,
      );

      const totalTripDistanceUom = G.ifElse(
        hasDistanceOverride,
        R.propOr(distanceOverrideUom, distanceOverrideUom, distanceOverrideUomMap),
        fallbackTotalTripDistanceUom,
      );

      const totalTripWeight = R.or(
        R.path([GC.FIELD_TOTAL_TRIP_WEIGHT], rateInfo),
        R.path([GC.FIELD_TOTAL_TRIP_WEIGHT], values),
      );

      const totalTripWeightUom = R.or(
        R.path([GC.FIELD_TOTAL_TRIP_WEIGHT_UOM], rateInfo),
        R.path([GC.FIELD_TOTAL_TRIP_WEIGHT_UOM], values),
      );

      const mode = R.or(
        R.path(['price', GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
        R.path([GC.FIELD_MODE], values),
      );

      const serviceType = R.pathOr(
        '',
        [GC.FIELD_DROPDOWN_OPTION_GUID],
        R.head(R.pathOr([], [GC.FIELD_PRICE, GC.FIELD_CARRIER_SERVICE_TYPES], data)),
      );

      const priceGuid = R.path(['price', 'contractGuid'], data);
      const currency = R.prop(GC.FIELD_CURRENCY, data);
      const serviceDays = R.prop(GC.FIELD_SERVICE_DAYS, data);
      const carrierContractGuid = G.getPropFromObject(GC.FIELD_CONTRACT_GUID, data);
      const originCarrierName = G.getPropFromObject(GC.FIELD_ORIGIN_CARRIER_NAME, data);
      const customerContractGuid = G.getPropFromObject(GC.FIELD_CUSTOMER_CONTRACT_GUID, data);
      const destinationCarrierName = G.getPropFromObject(GC.FIELD_DESTINATION_CARRIER_NAME, data);
      const transportingCarrierName = R.path([GC.FIELD_TRANSPORTING_CARRIER, GC.FIELD_NAME], data);

      let newValues = {
        ...values,
        originCarrierName,
        carrierContractGuid,
        customerContractGuid,
        [GC.FIELD_MODE]: mode,
        destinationCarrierName,
        transportingCarrierName,
        [GC.FIELD_CURRENCY]: currency,
        [GC.FIELD_SERVICE_TYPE]: serviceType,
        [GC.FIELD_SERVICE_DAYS]: serviceDays,
        [GC.FIELD_TOTAL_TRIP_WEIGHT]: totalTripWeight,
        [GC.FIELD_SELECTED_RATE_PRICE_GUID]: priceGuid,
        [GC.FIELD_TOTAL_TRIP_DISTANCE]: totalTripDistance,
        [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: totalTripWeightUom,
        [GC.FIELD_TOTAL_TRIP_DISTANCE_UOM]: totalTripDistanceUom,
        [GC.FIELD_CHARGES]: mapCharges2(G.getChargesFromObject(data)),
      };

      const { id } = data;

      const carrierContractData = R.find(R.propEq(id, 'id'), R.pathOr([], ['rateData'], rateInfo));

      if (G.isNotNilAndNotEmpty(carrierContractData)) {
        // NOTE: remember common logic with CarrierRateForm -> handleSelectAutoRate
        const { charges } = carrierContractData;

        const carrierAssignment = R.omit([GC.FIELD_GUID], getCarrierRateFields(carrierContractData));

        const mode = R.or(
          R.path(['price', GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], carrierContractData),
          R.path([GC.FIELD_MODE], values),
        );

        const serviceType = R.pathOr(
          '',
          [GC.FIELD_DROPDOWN_OPTION_GUID],
          R.head(R.pathOr([], ['price', 'serviceTypes'], carrierContractData)),
        );

        const carrierContractGuid = R.path(['price', 'contractGuid'], carrierContractData);

        const customerTotal = G.getPropFromObject2('totalCustomerRate', getOrderRateInfo(newValues), null);

        const staticFields = R.pick(
          [
            GC.FIELD_CURRENCY,
            GC.FIELD_SERVICE_DAYS,
            GC.FIELD_REQUESTED_PICKUP_EARLY_DATE,
            GC.FIELD_ESTIMATED_DELIVERY_LATE_DATE,
            GC.FIELD_ESTIMATED_DELIVERY_EARLY_DATE,
          ],
          carrierContractData,
        );

        const telCarrierRate = {
          ...staticFields,
          carrierAssignment,
          carrierContractGuid,
          [GC.FIELD_MODE]: mode,
          [GC.FIELD_SERVICE_TYPE]: serviceType,
          carrierRateCharges: mapCharges(charges),
          [GC.FIELD_CUSTOMER_TOTAL]: customerTotal,
          [GC.FIELD_TOTAL_TRIP_WEIGHT]: totalTripWeight,
          [GC.FIELD_TOTAL_TRIP_DISTANCE]: totalTripDistance,
          [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: totalTripWeightUom,
          [GC.FIELD_TOTAL_TRIP_DISTANCE_UOM]: totalTripDistanceUom,
        };

        newValues = R.assoc('telCarrierRate', telCarrierRate, newValues);

        G.callFunctionWithArgs(setCarrierRateToStore, telCarrierRate);
      }

      if (G.isOneNotNilOrNotEmpty([originCarrierName, transportingCarrierName, destinationCarrierName])) {
        const handleSelect = () => {
          setValues(newValues);
          G.callFunctionWith2Args(handleGetContractAccessorials, customerContractGuid, G.getChargesFromObject(data));

          G.callFunction(handleSetRecalculateAllChargesId);
          G.callFunctionWithArgs(handleCalculateETAOnRateSelect, newValues);
          G.callFunctionWithArgs(handleSetCustomerRateFromAvailableRates, newValues);
          G.callFunctionTwice(closeModal);
        };

        const component = (
          <SelectRateFromIntegration
            closeModal={closeModal}
            handleSelect={handleSelect}
            originCarrierName={originCarrierName}
            destinationCarrierName={destinationCarrierName}
            transportingCarrierName={transportingCarrierName}
          />
        );

        const modal = {
          p: 15,
          component,
          options: {},
        };

        return openModal(modal);
      }

      setValues(newValues);
      G.callFunctionWith2Args(handleGetContractAccessorials, customerContractGuid, G.getChargesFromObject(data));

      G.callFunction(handleSetRecalculateAllChargesId);
      G.callFunctionWithArgs(handleCalculateETAOnRateSelect, newValues);

      closeModal();
    },
  }),
  withHandlers({
    handleOpenAutoRateModal: (props) => () => {
      const {
        cloGuid,
        telGuid,
        loadData,
        openModal,
        isLegRate,
        branchGuid,
        closeModal,
        equipments,
        divisionGuid,
        handleSelectRate,
    } = props;

      const transportationMode = R.path(['values', GC.FIELD_MODE], props);
      const serviceType = R.path(['values', GC.FIELD_SERVICE_TYPE], props);

      const modalContent = (
        <EnhancedAvailableRates
          cloGuid={cloGuid}
          telGuid={telGuid}
          loadData={loadData}
          openModal={openModal}
          isLegRate={isLegRate}
          closeModal={closeModal}
          equipments={equipments}
          serviceType={serviceType}
          branchGuid={divisionGuid}
          onSelectRate={handleSelectRate}
          customerEnterpriseGuid={branchGuid}
          transportationMode={transportationMode}
        />
      );

      const modal = {
        p: '0',
        component: modalContent,
        options: {
          width: 'auto',
          minWidth: 500,
          height: 'auto',
          maxWidth: '90vw',
          overflow: 'auto',
          maxHeight: '80vh',
          minHeight: '50vh',
        },
      };

      openModal(modal);
    },
  }),
  pure,
);

const callOrderRateSyncWithTripsCallback = (fromPage, syncData, props) => {
  const { getLoadDetailsRequest, getOrderDetailsRequest, getLoadCustomerRateListRequest } = props;

  if (G.isPageDispatchDetailsNewLoad(fromPage)) {
    const telGuid = G.getPropFromObject(GC.FIELD_TEL_GUID, syncData);
    const args = { [GC.FIELD_LOAD_GUID]: telGuid };

    return G.callFunctionWithArgs(getLoadCustomerRateListRequest, args);
  }

  if (G.isPageDispatchBoardNew(fromPage)) {
    const telGuid = G.getPropFromObject(GC.FIELD_TEL_GUID, syncData);
    const cloGuid = G.getPropFromObject(GC.FIELD_CLO_GUID, syncData);

    if (G.isNotNilAndNotEmpty(telGuid)) {
      return G.callFunctionWithArgs(getLoadDetailsRequest, telGuid);
    }

    if (G.isNotNilAndNotEmpty(cloGuid)) {
      return G.callFunctionWithArgs(getOrderDetailsRequest, cloGuid);
    }
  }
};

const getOrderRateSyncGuid = (syncData, fieldName, formGuid, rateGuid) => {
  const syncGuid = G.getPropFromObject(fieldName, syncData);

  if (G.isNotNilAndNotEmpty(syncGuid)) return syncGuid;

  if (G.isNilOrEmpty(rateGuid)) return formGuid;

  return null;
};

const mergeLegRateWithUpliftResponse = R.mergeWith(
  (legRateValue, responseValue) => (
    R.isNil(responseValue) ? legRateValue : responseValue
  ),
);

const withOrderRateSyncWithTrips = ({ fromPage, updateValues }) => compose(
  connect(null, {
    openLoader,
    closeLoader,
    getLoadDetailsRequest,
    getOrderDetailsRequest,
    getLoadLegRateListRequest,
    getOrderLegRateListRequest,
    getOrderCostAllocationsRequest,
    getLoadCustomerRateListRequest,
    getOrderCustomerRateListRequest,
  }),
  withHandlers({
    handleSyncWithTrips: (props) => async (reqData, syncData) => {
      const {
        values,
        cloGuid,
        telGuid,
        setValues,
        openLoader,
        closeLoader,
        initialValues,
        isLegRate: isLegRateForm,
        getLoadLegRateListRequest,
        getOrderLegRateListRequest,
        legRateParentType: formLegRateParentType,
      } = props;

      const rateGuid = G.getGuidFromObject(initialValues);
      const isLegRate = R.or(isLegRateForm, R.prop('isLegRate', syncData));
      const selectedLegRate = R.prop('legRate', syncData);

      const shouldPersistLegRateUplift = G.isAllTrue(
        isLegRate,
        G.isAnyTrue(
          G.isPageDispatchBoardNew(fromPage),
          G.isPageDispatchDetailsNewLoad(fromPage),
        ),
        G.isNotNilAndNotEmpty(selectedLegRate),
      );

      const legRateParentType = R.or(
        formLegRateParentType,
        R.prop('legRateParentType', syncData),
      );

      const syncTelGuid = isLegRate
        ? R.or(G.getPropFromObject(GC.FIELD_TEL_GUID, syncData), telGuid)
        : getOrderRateSyncGuid(syncData, GC.FIELD_TEL_GUID, telGuid, rateGuid);

      const syncCloGuid = isLegRate
        ? R.or(G.getPropFromObject(GC.FIELD_CLO_GUID, syncData), cloGuid)
        : getOrderRateSyncGuid(syncData, GC.FIELD_CLO_GUID, cloGuid, rateGuid);

      if (G.isAllTrue(
        G.isNilOrEmpty(syncCloGuid),
        G.isNilOrEmpty(syncTelGuid),
        G.isNilOrEmpty(rateGuid),
      )) return;

      openLoader();

      const options = {
        data: G.ifElse(
          isLegRate,
          R.mergeRight(reqData, { cloGuid: syncCloGuid, telGuid: syncTelGuid }),
          reqData,
        ),
        params: G.ifElse(
          isLegRate,
          { telGuid: syncTelGuid },
          G.ifElse(
            G.isNotNilAndNotEmpty(syncTelGuid),
            { telGuid: syncTelGuid },
            G.ifElse(G.isNotNilAndNotEmpty(syncCloGuid), { cloGuid: syncCloGuid }, { rateGuid }),
          ),
        ),
      };

      const endpoint = G.ifElse(isLegRate, endpointsMap.legRateSyncWithTel, endpointsMap.cloRateSyncEndpoint);

      const method = G.ifElse(isLegRate, 'post', 'put');

      const res = await sendRequest(method, endpoint, options);

      const { data, status } = res;

      if (G.isAllTrue(G.isResponseSuccess(status), shouldPersistLegRateUplift)) {
        const updatedLegRate = mergeLegRateWithUpliftResponse(selectedLegRate, data);
        const updateOptions = { data: updatedLegRate };
        const updateRes = await sendRequest('put', endpointsMap.legRate, updateOptions);

        closeLoader();

        if (G.isResponseSuccess(updateRes.status)) {
          G.showToastrMessageFromLocale('success', 'messages:success:update');

          if (G.isPageDispatchDetailsNewLoad(fromPage)) {
            G.callFunctionWithArgs(getLoadLegRateListRequest, { loadGuid: syncTelGuid });
          }
        } else {
          G.handleFailResponseSimple(updateRes);
        }

        return;
      }

      closeLoader();

      if (G.isResponseSuccess(status)) {
        if (isLegRate) {
          const isTel = G.isLoadTypeTel(legRateParentType);

          const loadGuid = isTel ? syncTelGuid : syncCloGuid;
          const request = isTel ? getLoadLegRateListRequest : getOrderLegRateListRequest;

          G.callFunctionWithArgs(request, { loadGuid });
        } else {
          callOrderRateSyncWithTripsCallback(fromPage, syncData, props);
        }

        if (G.isFalsy(updateValues)) return;

        const newValues = R.assoc('charges', G.getChargesFromObject(data), values);

        setValues(newValues);
      } else {
        G.handleFailResponseSimple(res);
      }
    },
  }),
  pure,
);

export {
  withOrderRateSyncWithTrips,
  withAsyncCloRateAvailableRates,
  withHandleCloRateAvailableRates,
};
